import { Doctor, Patient, PortalMode } from './api';
import { getTodayISO } from '../utils/date';

// ============================================================
// Local Types for DispensePage
// ============================================================

export interface DummyItem {
  id: number;
  code: string;
  name: string;
  publishName: string;
  price: number;
  type: 'MED' | 'PRC' | 'LAB' | 'RAD';
  defaultQty: number;
  defaultDose?: string;
  freq?: string;
  instruction?: string;
  isChronic?: boolean;
}

// Tele Consult item code - auto-added on pick, not editable by doctor (matching CheckLine behavior)
export const TELE_CONSULT_ITEM_CODE = '1003';

/** Delivery fee item code (API auto-adds P-DEL at $10 for offsite tele when a med is added; users may edit price or remove) */
export const DELIVERY_FEE_ITEM_CODE = 'P-DEL';

// OrderItem structure matching CheckLine's VisitItemEntry
export interface OrderItem extends DummyItem {
  quantity: number; // Decimal - matching CheckLine
  unitPrice: number; // Decimal - matching CheckLine
  dosage?: number; // Decimal - matching CheckLine (tablets/dose)
  doseUOM?: string; // Unit of measurement for dosage (e.g., "tablet", "ml", "iu")
  dispenseUOM?: string; // Unit of measurement for quantity (e.g., "tablet", "bottle", "box")
  frequency?: string; // Matching CheckLine
  instruction?: string; // Matching CheckLine
  precaution?: string; // Matching CheckLine
  purpose?: string; // Matching CheckLine
  // Claimable flags - matching CheckLine's FormDispense SelectedOrderIs* properties
  isCHASClaimable?: boolean;
  isMedisaveClaimable?: boolean;
  isCDMPClaimable?: boolean;
  isManagedCare?: boolean;
  // Legacy field for backward compatibility
  qty?: number;
}

export interface Diagnosis {
  code: string;
  name: string;
}

export interface DiagnosisCategory {
  label: string;
  items: Diagnosis[];
}

export interface MCHistoryItem {
  id: number;
  date: string;
  store: string;
  doctor: string;
  fromDate: string;
  toDate: string;
  days: number;
  diagnosis: string;
}

export interface DispensePageProps {
  doctor: Doctor | null;
  patient: Patient;
  onBack: (notice?: string) => void;
  onLogout: () => void;
  isExternalConsult?: boolean;
  /** When `staff`, dispense is editable for any doctor-picked visit (not only the logged-in emp). */
  portalMode?: PortalMode;
}

// Alert/Confirm modal state types
export interface AlertModalState {
  show: boolean;
  message: string;
  type?: 'info' | 'success' | 'error';
}

export interface ConfirmModalState {
  show: boolean;
  message: string;
  onConfirm: () => void;
  onCancel?: () => void;
  confirmText?: string;
  confirmStyle?: string;
}

// Drug allergy form state
export interface DrugAllergyFormState {
  drugProbabilityCode: string;
  reactionCode: string;
  outcomeCode: string;
  informationSourceCode: string;
  drugAllergyIndicatorCode: string;
  alertIndicatorCode: string;
}

// Memo form state
export interface MemoFormState {
  recipient: string;
  subHeader: string;
  body: string;
  signOff: string;
  memoDate: string;
}

// Default values
export const DEFAULT_DRUG_ALLERGY_FORM: DrugAllergyFormState = {
  drugProbabilityCode: '1', // Default: Definite
  reactionCode: '0', // Default: Empty
  outcomeCode: '6', // Default: Medically Significant
  informationSourceCode: '03', // Default: Patient
  drugAllergyIndicatorCode: 'Y', // Default: Yes
  alertIndicatorCode: '2' // Default: Yes, avoid the same drug only
};

export const DEFAULT_MEMO_FORM: MemoFormState = {
  recipient: '',
  subHeader: '',
  body: '',
  signOff: '',
  memoDate: getTodayISO()
};

export const DEFAULT_ALERT_MODAL: AlertModalState = { show: false, message: '', type: 'info' };

export const DEFAULT_CONFIRM_MODAL: ConfirmModalState = { show: false, message: '', onConfirm: () => {} };
